/**
 * fieldSecurityApi — typed client for the real M02 §12 Field-Level Security
 * backend (`@Controller('v1/field-security')`, reached as `/api/field-security`).
 *
 * Each rule declares how a field on a screen is presented to a role:
 * HIDDEN | READ_ONLY | EDITABLE | MANDATORY.
 */
import { apiRequest } from './client.js';

/** §12 visibility settings — matches backend FieldVisibility enum. */
export const FIELD_VISIBILITIES = ['HIDDEN', 'READ_ONLY', 'EDITABLE', 'MANDATORY'] as const;
export type FieldVisibility = (typeof FIELD_VISIBILITIES)[number];

/** Human labels for the visibility codes. */
export const FIELD_VISIBILITY_LABELS: Readonly<Record<string, string>> = {
  HIDDEN: 'Hidden',
  READ_ONLY: 'Read only',
  EDITABLE: 'Editable',
  MANDATORY: 'Mandatory',
};

/** T076 field_security_rule row. */
export interface FieldSecurityRule {
  readonly ruleId: string;
  readonly roleCode: string;
  readonly screenCode: string;
  readonly fieldName: string;
  readonly visibility: string;
  readonly ruleNote?: string | null;
  readonly effectiveFromDate?: string;
  readonly effectiveToDate?: string;
}

export interface FieldRuleQuery {
  readonly roleCode?: string;
  readonly screenCode?: string;
  readonly fieldName?: string;
}

export interface UpsertFieldRuleInput {
  readonly roleCode: string;
  readonly screenCode: string;
  readonly fieldName: string;
  readonly visibility: FieldVisibility;
  readonly ruleNote?: string;
}

type Tok = string | null | undefined;

/** GET /v1/field-security/rules — list rules, optionally filtered. */
export function listFieldRules(query: FieldRuleQuery = {}, token?: Tok): Promise<readonly FieldSecurityRule[]> {
  const qs = new URLSearchParams();
  if (query.roleCode) qs.set('roleCode', query.roleCode);
  if (query.screenCode) qs.set('screenCode', query.screenCode);
  if (query.fieldName) qs.set('fieldName', query.fieldName);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiRequest<readonly FieldSecurityRule[]>(`/field-security/rules${suffix}`, { token });
}

/** POST /v1/field-security/rules — create or update a rule. */
export function upsertFieldRule(body: UpsertFieldRuleInput, token?: Tok): Promise<FieldSecurityRule> {
  return apiRequest<FieldSecurityRule>('/field-security/rules', { method: 'POST', token, body });
}

/** DELETE /v1/field-security/rules/:ruleId — deactivate a rule. */
export function deactivateFieldRule(ruleId: string, token?: Tok): Promise<FieldSecurityRule> {
  return apiRequest<FieldSecurityRule>(`/field-security/rules/${ruleId}`, { method: 'DELETE', token });
}
